/* De lijst van Organisaties (/crm/organisaties): ophalen, tabs, zoeken, sorteren en de
 * filter in de URL. Zelfde opzet als de projectlijst, de offertelijst en Locaties.
 *
 * Wat er aan een organisatie hangt komt uit losse vragen (geen embed): contacten,
 * projecten, en Charging (Opportunities en Kavels). Op 29-09-2026: 125 organisaties,
 * 16 met een project, ~70 uit Charging, 18 leveranciers.
 *
 * Relatieve imports (geen @/), zodat de toetsen dit bestand zonder bundel lezen. */

import { lijstGeheugen, vindt } from './geheugen';
import { adressenUit, contactNaam, domein, plaatsVan, sorteerNaam } from './labels';

export interface RelatieClient {
  from(tabel: string): any;
}

export interface OrgProject {
  id: string;
  nummer: string | null;
  naam: string;
  stage: string | null;
}

export interface LijstOrganisatie {
  id: string;
  naam: string;
  sector: string | null;
  website: string | null;
  plaats: string | null;
  kvk: string | null;
  status: string | null;
  classificatie: string | null;
  icoon: string | null;
  contacten: { id: string; naam: string }[];
  projecten: OrgProject[];
  /** Opportunities en Kavels in Charging. */
  charging: number;
  aangemaakt: string;
  bijgewerkt: string | null;
  /** De rij zoals hij in de database staat, voor OrganizationModal. */
  bron: Record<string, any>;
}

const nummerVan = (s: string | null | undefined) => {
  const m = /(\d+)/.exec(s ?? '');
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
};

export async function haalOrganisaties(client: RelatieClient): Promise<LijstOrganisatie[]> {
  const orgs = await client.from('organizations').select('*').is('deleted_at', null);
  if (orgs.error) throw orgs.error;
  const [contacten, projecten, kansen, kavels] = await Promise.all([
    client.from('contacts').select('id, org_id, first_name, last_name').is('deleted_at', null).not('org_id', 'is', null),
    client.from('projects').select('id, org_id, project_number, name, stage').is('deleted_at', null).not('org_id', 'is', null),
    client.from('charging_opportunities').select('id, org_id').is('deleted_at', null).not('org_id', 'is', null),
    client.from('charging_locations').select('id, org_id').is('deleted_at', null).not('org_id', 'is', null),
  ]);
  for (const r of [contacten, projecten, kansen, kavels]) if (r.error) throw r.error;

  const perOrg = <T,>(rijen: any[], maak: (r: any) => T) => {
    const m = new Map<string, T[]>();
    for (const r of rijen ?? []) {
      const lijst = m.get(r.org_id) ?? [];
      lijst.push(maak(r));
      m.set(r.org_id, lijst);
    }
    return m;
  };
  const contactenPer = perOrg(contacten.data, (r) => ({ id: r.id, naam: contactNaam(r.first_name, r.last_name), achternaam: sorteerNaam(r.last_name, r.first_name) }));
  const projectenPer = perOrg(projecten.data, (r): OrgProject => ({ id: r.id, nummer: r.project_number || null, naam: r.name ?? '(zonder naam)', stage: r.stage ?? null }));
  const chargingPer = new Map<string, number>();
  for (const r of [...(kansen.data ?? []), ...(kavels.data ?? [])]) chargingPer.set(r.org_id, (chargingPer.get(r.org_id) ?? 0) + 1);

  return (orgs.data ?? []).map((o: any) => ({
    id: String(o.id),
    naam: o.name?.trim() || '(zonder naam)',
    sector: o.sector?.trim() || null,
    website: o.website?.trim() || null,
    plaats: plaatsVan(adressenUit(o.addresses)),
    kvk: o.kvk?.trim() || null,
    status: o.status ?? null,
    classificatie: o.classification ?? null,
    icoon: o.cached_favicon_url || o.favicon_url || o.logo_url || null,
    contacten: (contactenPer.get(o.id) ?? [])
      .sort((a, b) => a.achternaam.localeCompare(b.achternaam, 'nl'))
      .map(({ id, naam }) => ({ id, naam })),
    projecten: (projectenPer.get(o.id) ?? []).sort((a, b) => nummerVan(a.nummer) - nummerVan(b.nummer)),
    charging: chargingPer.get(o.id) ?? 0,
    aangemaakt: o.created_at,
    bijgewerkt: o.updated_at ?? null,
    bron: o,
  }));
}

/* ── tabs ─────────────────────────────────────────────────────────── */

export const TABS = ['alle', 'projecten', 'charging', 'leveranciers'] as const;
export type Tab = (typeof TABS)[number];

export const TAB_LABEL: Record<Tab, string> = {
  alle: 'Alle',
  projecten: 'Met project',
  charging: 'Charging',
  leveranciers: 'Leveranciers',
};

export function inTab(o: LijstOrganisatie, tab: Tab): boolean {
  switch (tab) {
    case 'alle': return true;
    case 'projecten': return o.projecten.length > 0;
    case 'charging': return o.charging > 0;
    case 'leveranciers': return o.classificatie === 'leverancier';
  }
}

/* ── zoeken en sorteren ───────────────────────────────────────────── */

export type Sortering = 'naam' | 'projecten' | 'bijgewerkt';
export const SORTERINGEN: Sortering[] = ['naam', 'projecten', 'bijgewerkt'];

export interface LijstFilter {
  zoek: string;
  tab: Tab;
  sortering: Sortering;
  oplopend: boolean;
}

export const STANDAARD_FILTER: LijstFilter = { zoek: '', tab: 'alle', sortering: 'naam', oplopend: true };

export function passendBijZoek(o: LijstOrganisatie, zoek: string): boolean {
  return vindt(zoek, [
    o.naam, o.sector, o.plaats, o.kvk, domein(o.website),
    ...o.contacten.map((c) => c.naam),
    ...o.projecten.flatMap((p) => [p.nummer, p.naam]),
  ]);
}

export function filterEnSorteer(lijst: LijstOrganisatie[], f: LijstFilter): LijstOrganisatie[] {
  const uit = lijst.filter((o) => inTab(o, f.tab) && passendBijZoek(o, f.zoek));
  const richting = f.oplopend ? 1 : -1;
  const opNaam = (a: LijstOrganisatie, b: LijstOrganisatie) => a.naam.localeCompare(b.naam, 'nl', { sensitivity: 'base' });
  return uit.sort((a, b) => {
    switch (f.sortering) {
      case 'projecten': return richting * (a.projecten.length - b.projecten.length) || opNaam(a, b);
      case 'bijgewerkt': {
        const x = a.bijgewerkt ?? a.aangemaakt;
        const y = b.bijgewerkt ?? b.aangemaakt;
        return (x === y ? 0 : x < y ? -richting : richting) || opNaam(a, b);
      }
      default: return richting * opNaam(a, b);
    }
  });
}

export interface TabTelling {
  tab: Tab;
  aantal: number;
}

/** Telling per tab binnen de zoekterm, zodat de tabs kloppen met wat je ziet. */
export function tellingen(lijst: LijstOrganisatie[], zoek: string): TabTelling[] {
  const basis = lijst.filter((o) => passendBijZoek(o, zoek));
  return TABS.map((tab) => ({ tab, aantal: basis.filter((o) => inTab(o, tab)).length }));
}

/** Bij een nieuwe sortering: naam oplopend, aantallen en datums aflopend. */
export const beginRichting = (s: Sortering) => s === 'naam';

/* ── filter in de URL ─────────────────────────────────────────────── */

export function filterUitUrl(p: URLSearchParams): LijstFilter {
  const tab = p.get('tab');
  const sort = p.get('sort') as Sortering;
  const sortering = SORTERINGEN.includes(sort) ? sort : STANDAARD_FILTER.sortering;
  const dir = p.get('dir');
  return {
    zoek: p.get('q') ?? '',
    tab: TABS.includes(tab as Tab) ? (tab as Tab) : STANDAARD_FILTER.tab,
    sortering,
    oplopend: dir === 'asc' ? true : dir === 'desc' ? false : beginRichting(sortering),
  };
}

export function filterNaarUrl(f: LijstFilter): Record<string, string> {
  const uit: Record<string, string> = {};
  if (f.zoek) uit.q = f.zoek;
  if (f.tab !== STANDAARD_FILTER.tab) uit.tab = f.tab;
  if (f.sortering !== STANDAARD_FILTER.sortering) uit.sort = f.sortering;
  if (f.oplopend !== beginRichting(f.sortering)) uit.dir = f.oplopend ? 'asc' : 'desc';
  return uit;
}

export const organisatieLijst = lijstGeheugen('organisaties', '/crm/organisaties');
